import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import {
  HomeIcon,
  CalendarIcon,
  CreateIcon,
  AccountsIcon,
  AlertIcon,
  SparklesIcon,
} from './Icons';
import { useUiStore } from '../store/uiStore';
import { usePostsStore } from '../store/postsStore';
import { useI18n } from '../i18n/I18nProvider';

// Posts — feed / media grid (sidebar only; PostsIcon stays for stats etc.).
const PostsNavIcon = (props) => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="3" y="3" width="8" height="8" rx="1.5" />
    <rect x="13" y="3" width="8" height="8" rx="1.5" />
    <path d="M3 14h18M3 18h12M3 21h15" />
  </svg>
);

// Templates — layout / wireframe (distinct from AI sparkles).
const TemplatesNavIcon = (props) => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18M9 21V9" />
  </svg>
);

// Tiny inline icon — avoids extending Icons.jsx for one nav item.
const MegaphoneIcon = (props) => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="m3 11 18-5v12L3 14v-3z" />
    <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
  </svg>
);

// Inline icon for Competitors — a small line-chart glyph.
const ChartIcon = (props) => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M3 3v18h18" />
    <path d="m7 14 4-4 3 3 5-6" />
  </svg>
);

// Smart Planner — schedule blocks (distinct from Templates / AI sparkles).
const SmartPlannerIcon = (props) => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="3" y="3" width="8" height="18" rx="1.5" />
    <rect x="14" y="3" width="7" height="6" rx="1.5" />
    <rect x="14" y="11" width="7" height="10" rx="1.5" />
  </svg>
);

// Order matches product IA: core publish → content → growth → system.
const NAV_ITEMS = [
  { to: '/dashboard', labelKey: 'common.dashboard', Icon: HomeIcon },
  { to: '/create', labelKey: 'common.createPost', Icon: CreateIcon },
  { to: '/posts', labelKey: 'common.posts', Icon: PostsNavIcon },
  { to: '/inbox', labelKey: 'common.messagesComments', Icon: AlertIcon },
  { to: '/campaigns', labelKey: 'common.campaigns', Icon: MegaphoneIcon },
  { to: '/planner', labelKey: 'common.smartPlanner', Icon: SmartPlannerIcon },
  { to: '/ai', labelKey: 'common.aiAssistant', Icon: SparklesIcon },
  { to: '/templates', labelKey: 'common.templates', Icon: TemplatesNavIcon },
  { to: '/calendar', labelKey: 'common.calendar', Icon: CalendarIcon },
  { to: '/competitors', labelKey: 'common.competitors', Icon: ChartIcon },
  { to: '/accounts', labelKey: 'common.socialAccounts', Icon: AccountsIcon },
  { to: '/failures', labelKey: 'common.failures', Icon: AlertIcon, showFailuresBadge: true },
];

export default function Sidebar() {
  const { t, isArabic } = useI18n();
  const sidebarOpen = useUiStore((s) => s.sidebarOpen);
  const setSidebarOpen = useUiStore((s) => s.setSidebarOpen);
  // Failures count drives the sidebar badge. We hydrate once on mount so
  // the number is ready the first time the user opens the menu.
  const failuresCount = usePostsStore((s) => s.failures.length);
  const fetchFailures = usePostsStore((s) => s.fetchFailures);

  useEffect(() => {
    // Best-effort background hydrate — errors here are silent.
    fetchFailures().catch(() => {});
  }, [fetchFailures]);

  return (
    <>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-gradient-to-br from-blue-950/40 via-slate-950/50 to-sky-950/40 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 z-40 w-64 transform bg-white/80 shadow-glow backdrop-blur-xl transition-transform duration-200 ease-out dark:bg-slate-900/85 dark:shadow-card-dark lg:translate-x-0 ${
          isArabic ? 'right-0 border-l border-white/40 dark:border-slate-700/60' : 'left-0 border-r border-white/40 dark:border-slate-700/60'
        } ${
          sidebarOpen
            ? 'translate-x-0'
            : isArabic
              ? 'translate-x-full'
              : '-translate-x-full'
        }`}
      >
        <div className="flex h-16 items-center border-b border-slate-200/80 bg-gradient-to-r from-blue-500/[0.07] via-brand-500/[0.05] to-sky-500/[0.05] px-5 dark:border-slate-700/80 dark:from-blue-500/12 dark:via-brand-500/8 dark:to-sky-500/10">
          <img
            src="/WhatsApp_Image_2026-04-08_at_11.55.06_AM__1_-removebg-preview.png"
            alt="Garawud logo"
            className="h-10 w-auto object-contain"
          />
        </div>

        <nav className="flex flex-col gap-0.5 p-3 pb-28">
          {NAV_ITEMS.map(({ to, labelKey, Icon, showFailuresBadge }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/dashboard'}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `nav-link ${isActive ? 'nav-link-active' : ''}`
              }
            >
              <Icon />
              <span className="flex-1">{t(labelKey)}</span>
              {showFailuresBadge && failuresCount > 0 && (
                <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-100 px-1.5 text-[10px] font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-300">
                  {failuresCount > 99 ? '99+' : failuresCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="absolute inset-x-3 bottom-3 rounded-2xl border border-white/50 bg-gradient-to-br from-blue-100/90 via-sky-50/85 to-cyan-50/70 p-4 shadow-md ring-1 ring-blue-300/20 dark:border-slate-700/60 dark:from-blue-950/40 dark:via-sky-950/30 dark:to-slate-950/80 dark:ring-sky-500/20">
          <p className="text-xs font-semibold tracking-tight text-gray-900 dark:text-gray-100">
            {t('sidebar.proTipTitle')}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t('sidebar.proTipBody')}
          </p>
        </div>
      </aside>
    </>
  );
}
